import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { IconShieldCheck, IconShieldX } from "@tabler/icons-react";
import { api } from "@/lib/api";

/**
 * Public embeddable badge for a certified Sello Sombra.
 * Designed to be embedded via <iframe src="/embed/sello/{code}"> in Instagram
 * bios, personal sites, etc. No site nav, no auth, compact 320x120 layout.
 */
export default function SelloBadge() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/sello/verify/${code}`);
        setData(data);
      } catch (e) {
        setError(e?.response?.status === 404 ? "not_found" : "error");
      } finally { setLoading(false); }
    })();
  }, [code]);

  const verifyUrl = `${window.location.origin}/verificar/${code}`;

  if (loading) {
    return (
      <div style={styles.wrapper}>
        <div style={styles.loading} data-testid="badge-loading">Verificando…</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={styles.wrapper}>
        <a href={verifyUrl} target="_blank" rel="noreferrer" style={{ ...styles.badge, borderLeft: "4px solid #C0392B" }} data-testid="badge-invalid">
          <IconShieldX size={28} color="#C0392B" />
          <div style={styles.body}>
            <div style={{ ...styles.tag, background: "#FCECEA", color: "#B03A2E" }}>SELLO NO VIGENTE</div>
            <div style={styles.brand}>Chiessa Institute</div>
            <div style={styles.meta}>Verificar en chiessainstitute.com</div>
          </div>
        </a>
      </div>
    );
  }

  return (
    <div style={styles.wrapper}>
      <a href={verifyUrl} target="_blank" rel="noreferrer" style={styles.badge} data-testid="badge-valid">
        <IconShieldCheck size={32} color="#0A3F33" />
        <div style={styles.body}>
          <div style={styles.tag}>SELLO VIGENTE · CHIESSA INSTITUTE</div>
          <div style={styles.name}>{data.name}</div>
          <div style={styles.meta}>
            {(data.specialties || []).slice(0, 3).join(" · ") || "Maestra Sombra Certificada"}
          </div>
          <div style={styles.code}>Código {data.verification_code}</div>
        </div>
        <div style={styles.arrow}>↗</div>
      </a>
    </div>
  );
}

const teal = "#0A3F33";
const honey = "#C99148";

const styles = {
  wrapper: {
    margin: 0,
    padding: 0,
    fontFamily: "'Inter', system-ui, sans-serif",
    background: "transparent",
  },
  badge: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
    padding: "16px 18px",
    background: "#FFFDF7",
    borderRadius: "12px",
    border: `1px solid rgba(10,63,51,0.14)`,
    borderLeft: `4px solid ${honey}`,
    boxShadow: "0 2px 12px rgba(10,63,51,0.08)",
    textDecoration: "none",
    color: teal,
    maxWidth: "420px",
    transition: "transform .2s ease, box-shadow .2s ease",
  },
  body: { flex: 1, minWidth: 0 },
  tag: {
    display: "inline-block",
    background: honey,
    color: "#fff",
    fontSize: "9.5px",
    fontWeight: 700,
    letterSpacing: "0.08em",
    padding: "3px 8px",
    borderRadius: "4px",
    marginBottom: "6px",
  },
  brand: {
    fontFamily: "'Fraunces', 'Playfair Display', Georgia, serif",
    fontSize: "17px",
    fontWeight: 500,
    color: teal,
    lineHeight: 1.15,
  },
  name: {
    fontFamily: "'Fraunces', 'Playfair Display', Georgia, serif",
    fontSize: "17px",
    fontWeight: 500,
    color: teal,
    lineHeight: 1.15,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  meta: {
    fontSize: "11.5px",
    color: "#5A6460",
    marginTop: "3px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  code: {
    fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
    fontSize: "10.5px",
    color: teal,
    marginTop: "6px",
    fontWeight: 500,
  },
  arrow: {
    fontSize: "20px",
    color: teal,
    opacity: 0.5,
  },
  loading: {
    padding: "20px",
    fontSize: "11.5px",
    color: "#5A6460",
    textAlign: "center",
  },
};
